import { useCallback, useEffect, useRef, useState } from 'react';
import { SUPABASE_URL } from '../services/supabase';

/**
 * ¿Hay conexión DE VERDAD? No lo que dice `navigator.onLine`.
 *
 * `navigator.onLine` solo afirma que el aparato tiene una interfaz de red
 * levantada. No dice nada de si hay internet. En una obra eso falla justo en
 * los casos que importan:
 *
 *   · Wifi de la caseta sin salida a internet → `onLine: true`, nada carga.
 *   · Portal cautivo (el wifi del hotel, el del cliente) que intercepta todo
 *     → `onLine: true`, y cada petición devuelve la página de login del portal.
 *   · Dos rayitas de datos que no llegan a completar una petición → `onLine:
 *     true` durante todo el rato en que la app no puede guardar nada.
 *
 * Por eso aquí se PREGUNTA al servidor, que es el único que puede contestar
 * que sí de verdad. `navigator.onLine` se sigue usando, pero solo para lo que
 * sirve: cuando dice que NO, es que no, y ahí no hace falta gastar una
 * petición.
 *
 * Se pregunta a Supabase y no a nuestro propio origen a propósito: en
 * desarrollo el origen es `localhost`, que responde aunque el portátil esté
 * sin internet. Lo que importa no es «llega la red», es «llega la base de
 * datos», que es de donde sale y a donde va todo.
 */

/** Cada cuánto se vuelve a preguntar mientras la app está a la vista. */
const CADA = 25_000;
/** Si no contesta en este tiempo, se da por caído: en obra, esperar 30 s a un
 *  servidor no es «lento», es «no hay». */
const PACIENCIA = 6_000;

export interface Conexion {
    /** `false` solo cuando se ha comprobado que no llega. */
    enLinea: boolean;
    /** Hay una comprobación en marcha. Para no parpadear el aviso al reintentar. */
    comprobando: boolean;
    /** Fuerza una comprobación (el botón «reintentar»). */
    comprobar: () => void;
}

/**
 * ¿Contesta el servidor? Cualquier respuesta vale, incluso un error.
 *
 * La pregunta es «¿llega la petición?», no «¿tengo permiso?». Este endpoint
 * responde 401 sin la llave —comprobado contra el proyecto real— así que
 * exigir `r.ok` haría que la app se declarara sin conexión SIEMPRE. Un 401 es
 * la mejor prueba posible de que hay línea: alguien al otro lado leyó la
 * petición y se molestó en rechazarla.
 *
 * Solo cuenta como caída que `fetch` LANCE: ahí no hubo respuesta —DNS que no
 * resuelve, ruta que no existe, o los 6 segundos de paciencia agotados—.
 *
 * De regalo, esto también caza los portales cautivos: al ser una petición a
 * otro origen, la página de login que devuelve el portal no trae las cabeceras
 * CORS que hacen falta, `fetch` lanza, y la app se entera de que ese wifi no
 * lleva a ninguna parte. Con `navigator.onLine` esos casos salían como «hay
 * conexión».
 */
async function llega(señal: AbortSignal): Promise<boolean> {
    if (!SUPABASE_URL) return true;   // sin URL configurada no hay nada que medir
    try {
        // `no-store` para que no conteste una respuesta cacheada de hace rato,
        // que es exactamente el error que se quiere evitar.
        await fetch(`${SUPABASE_URL}/auth/v1/health`, {
            method: 'GET',
            cache: 'no-store',
            signal: señal,
        });
        return true;
    } catch {
        return false;
    }
}

export function useConexion(): Conexion {
    const [enLinea, setEnLinea] = useState(true);
    const [comprobando, setComprobando] = useState(false);
    const enVuelo = useRef<AbortController | null>(null);

    const comprobar = useCallback(async () => {
        // El navegador ya sabe que no hay red: no se gasta una petición.
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            setEnLinea(false);
            return;
        }
        enVuelo.current?.abort();
        const ctrl = new AbortController();
        enVuelo.current = ctrl;
        const reloj = window.setTimeout(() => ctrl.abort(), PACIENCIA);

        setComprobando(true);
        const ok = await llega(ctrl.signal);
        window.clearTimeout(reloj);
        if (ctrl.signal.aborted && enVuelo.current !== ctrl) return;  // llegó otra después
        setEnLinea(ok);
        setComprobando(false);
    }, []);

    useEffect(() => {
        comprobar();

        const alVolver = () => comprobar();
        const alIrse = () => setEnLinea(false);
        /* Al volver a la pestaña se pregunta enseguida: es cuando el usuario
           mira, y el estado puede llevar minutos sin refrescarse porque el
           temporizador no corre en una pestaña oculta. */
        const alMirar = () => { if (document.visibilityState === 'visible') comprobar(); };

        window.addEventListener('online', alVolver);
        window.addEventListener('offline', alIrse);
        document.addEventListener('visibilitychange', alMirar);
        const id = window.setInterval(() => {
            if (document.visibilityState === 'visible') comprobar();
        }, CADA);

        return () => {
            window.removeEventListener('online', alVolver);
            window.removeEventListener('offline', alIrse);
            document.removeEventListener('visibilitychange', alMirar);
            window.clearInterval(id);
            enVuelo.current?.abort();
        };
    }, [comprobar]);

    return { enLinea, comprobando, comprobar };
}
